import { z } from 'zod';
import { InsuranceTypeSchema, StoredDecisionSchema } from './eligibility-rules.js';
import { LocalDateSchema, StateCodeSchema, TimeZoneSchema } from './scheduling.js';
/**
 * Booking drafts (M5.2, ADR-018, ARCHITECTURE.md §13, FR-BOOK-09/10, API.md §2.3).
 *
 * ## What a draft is
 *
 * The booking funnel has no session. FR-BOOK-09 forbids carrying the patient's answers
 * in a URL, a query string, a fragment, `localStorage` or a third-party tag, and
 * ADR-018 puts them on the server instead: one `booking_drafts` row per anonymous
 * visitor, keyed by the signed HttpOnly `ha_draft` cookie, autosaved on every field
 * change and resumable for seven days.
 *
 * ## The one rule the shapes here exist to enforce
 *
 * **The draft is split in two, and the split is the contract.**
 *
 * - `DraftPatch` is what the browser sends. It carries PHI - a name, an email, a date
 *   of birth, an insurance member id - and the server encrypts every one of those
 *   fields into `booking_drafts.data_enc` (DATABASE.md §5).
 * - `DraftSummary` is what `GET /public/drafts/current` answers on page load, for the
 *   "Continue where you left off?" prompt. It carries **no PHI at all**: which step,
 *   which state, which visit type, whether details exist, and when it expires. It is
 *   a boolean `hasDetails`, never the details.
 * - `DraftContent` is the decrypted answers, and it is returned by exactly one route -
 *   `POST /public/drafts/current/resume`, the deliberate act of a visitor who has been
 *   shown the prompt and pressed "continue". Page load never sees it.
 *
 * Splitting them at the schema level is what makes "the resume prompt cannot leak PHI"
 * a property a reader can check in one place, rather than a discipline every handler
 * has to keep.
 *
 * ## Merging
 *
 * `PATCH /public/drafts/current` is debounce-friendly and idempotent: every section is
 * optional, a section that is present replaces its own fields one by one, and sending
 * the same patch twice leaves the row exactly as it was. There is no deep merge of
 * arbitrary json - the sections below are the whole vocabulary, which is what lets the
 * server validate per step instead of storing whatever it was handed.
 */
/* --------------------------------------------------------------------- steps ---- */
/**
 * The funnel's steps, in order. `booking_drafts.step` holds one of these and the
 * resume prompt sends the visitor back to it.
 *
 * The order matters twice: the funnel report walks it to compute drop-off (M5.8), and
 * `furthestStep` never moves backwards, so a patient who returns to fix their state
 * does not look like a patient who abandoned at step one.
 */
export const DRAFT_STEPS = [
    'state',
    'type',
    'date',
    'slot',
    'details',
    'insurance',
    'payment',
    'intake',
    'review',
    'confirmed',
];
export const DraftStepSchema = z.enum(DRAFT_STEPS);
/** Position in `DRAFT_STEPS`, or `-1`. Used to keep `furthestStep` monotonic. */
export function draftStepIndex(step) {
    if (step === null || step === undefined)
        return -1;
    return DRAFT_STEPS.indexOf(step);
}
export const DRAFT_STATUSES = ['active', 'converted', 'abandoned', 'expired'];
export const DraftStatusSchema = z.enum(DRAFT_STATUSES);
/* ------------------------------------------------------------------ sections ---- */
/**
 * The patient's own details. **Every field here is PHI** and is encrypted into
 * `data_enc`; none of it appears in `summary`, in a log line or in an analytics event.
 *
 * The shapes mirror `BookingPatientSchema` (scheduling.ts) so that a complete draft can
 * be handed straight to `POST /public/bookings/confirm` without a second translation -
 * but every field is optional here, because a draft exists precisely so that a
 * half-typed form survives a reload.
 */
export const DraftDetailsSchema = z
    .object({
    firstName: z.string().max(80).optional(),
    middleName: z.string().max(80).optional(),
    lastName: z.string().max(80).optional(),
    /** ISO `YYYY-MM-DD`, in the patient's own reckoning; never a timestamp. */
    dateOfBirth: LocalDateSchema.optional(),
    email: z.string().max(254).optional(),
    /** Accepted as typed; `confirm` is where the E.164 rule is enforced. */
    phone: z.string().max(32).optional(),
    sex: z.enum(['female', 'male', 'other', 'unknown']).optional(),
    timeZone: TimeZoneSchema.optional(),
    preferredLanguage: z.string().min(2).max(10).optional(),
})
    .strict();
/**
 * What the patient intends to do about paying. `memberId` and `groupNumber` are PHI;
 * the *intent* is not, and only the intent reaches `summary` (as `hasInsurance`).
 */
export const DraftInsuranceSchema = z
    .object({
    intent: z.enum(['self_pay', 'insurance', 'undecided']).optional(),
    /**
     * The plan type as the patient declares it (M15, `InsuranceTypeSchema`). Not PHI: a
     * category, mirrored to `summary.insuranceType` so the decision engine can read it.
     */
    insuranceType: InsuranceTypeSchema.optional(),
    payerId: z.string().max(64).optional(),
    payerName: z.string().max(200).optional(),
    memberId: z.string().max(64).optional(),
    groupNumber: z.string().max(64).optional(),
})
    .strict();
/**
 * A consent the patient ticked in the funnel. Not PHI: a kind and a document version.
 *
 * Nothing here *is* the consent record - `consents` rows are written at confirm, are
 * immutable and carry the accepted-at instant (DATABASE.md §7.4). This is the draft's
 * memory of which boxes were ticked, so that a reload does not silently untick them.
 */
export const DRAFT_CONSENT_KINDS = [
    'telehealth',
    'hipaa_notice',
    'financial',
    'unsecured_email',
    'sms',
    'marketing',
];
export const DraftConsentKindSchema = z.enum(DRAFT_CONSENT_KINDS);
export const DraftConsentSchema = z
    .object({
    kind: DraftConsentKindSchema,
    documentVersion: z.string().min(1).max(40),
    accepted: z.boolean(),
})
    .strict();
/** The chosen time. Instants, never a local string, so a zone change cannot move it. */
export const DraftSlotSchema = z
    .object({
    startAt: z.iso.datetime({ offset: true }),
    endAt: z.iso.datetime({ offset: true }).optional(),
})
    .strict();
/* --------------------------------------------------------------------- patch ---- */
/**
 * `PATCH /public/drafts/current`.
 *
 * Every section is optional and every section is **replace-by-field**: `details: {
 * firstName: 'Ada' }` changes the first name and leaves the surname alone, while
 * `slot: null` clears the slot. `.strict()` everywhere is deliberate - an unknown key
 * is a `400`, not something quietly stored, because "whatever the client sent" is how
 * a draft ends up holding a field nobody audited (rules §25.1).
 */
export const DraftPatchSchema = z
    .object({
    /** Where the patient is now. Never moves `furthestStep` backwards. */
    step: DraftStepSchema.optional(),
    /** Two-letter state of residence; the state gate's answer (FR-BOOK-03). */
    state: StateCodeSchema.nullable().optional(),
    /** The visit type's stable key, never its uuid - a marketing link carries the key. */
    appointmentTypeKey: z.string().min(1).max(64).nullable().optional(),
    /** Clinic-local date the calendar is showing, `YYYY-MM-DD`. */
    date: LocalDateSchema.nullable().optional(),
    slot: DraftSlotSchema.nullable().optional(),
    details: DraftDetailsSchema.optional(),
    insurance: DraftInsuranceSchema.optional(),
    /** Replaces the whole list; a consent absent from it is treated as unticked. */
    consents: z.array(DraftConsentSchema).max(16).optional(),
    /** Which improvement variants this visitor is seeing (`funnel.fewerSteps`, ...). */
    variants: z.array(z.string().min(1).max(64)).max(32).optional(),
})
    .strict()
    .refine((value) => Object.keys(value).length > 0, {
    message: 'Send at least one field to save.',
});
/** `POST /public/drafts`. Nothing is required: the cookie is the identity. */
export const CreateDraftRequestSchema = z
    .object({
    /**
     * The first step the funnel is showing. A landing page that deep-links to a visit
     * type may pass `type`; everything else starts at `state`.
     */
    step: DraftStepSchema.optional(),
    state: StateCodeSchema.optional(),
    appointmentTypeKey: z.string().min(1).max(64).optional(),
    /**
     * The funnel page's own URL, so the server can read `utm_*` from it when the
     * browser sent no `Referer`. Parsed for campaign parameters and then discarded -
     * it is never stored, and FR-BOOK-09 means it never carries patient data either.
     */
    landingUrl: z.string().max(2048).optional(),
})
    .strict();
/* ------------------------------------------------------------------- summary ---- */
/**
 * The attribution captured once, on the request that created the draft, and never
 * overwritten. Campaign strings only: no click ids, no identifiers, no referrer path.
 */
export const DraftAttributionSchema = z.object({
    source: z.string().nullable(),
    medium: z.string().nullable(),
    campaign: z.string().nullable(),
    term: z.string().nullable(),
    content: z.string().nullable(),
});
/** The hold this draft has on a slot, as the resume prompt needs to see it. */
export const DraftReservationSchema = z.object({
    id: z.string(),
    startAt: z.string(),
    endAt: z.string(),
    expiresAt: z.string(),
    /** False once the hold has expired or been consumed: the slot must be re-picked. */
    active: z.boolean(),
});
/**
 * **The no-PHI answer.** What `GET /public/drafts/current` returns on page load.
 *
 * `hasDetails` and `hasInsurance` are booleans on purpose: the prompt needs to know
 * that there is something to come back to, and nothing more. A reviewer can read this
 * object and see that a name cannot reach it.
 */
export const DraftSummarySchema = z.object({
    id: z.string(),
    status: DraftStatusSchema,
    /** Where the patient was. Null on a draft created before the first step was shown. */
    step: DraftStepSchema.nullable(),
    /** The furthest step reached, for the funnel report. Never moves backwards. */
    furthestStep: DraftStepSchema.nullable(),
    state: z.string().nullable(),
    appointmentTypeKey: z.string().nullable(),
    date: z.string().nullable(),
    selectedSlot: DraftSlotSchema.nullable(),
    reservation: DraftReservationSchema.nullable(),
    hasDetails: z.boolean(),
    hasInsurance: z.boolean(),
    /** M15: the declared plan type, null until the insurance step asked. */
    insuranceType: InsuranceTypeSchema.nullable().default(null),
    /** M15: the latest scheduling decision for this draft, null until one ran. */
    decision: StoredDecisionSchema.nullable().default(null),
    /** Consent kinds ticked. A kind and a version are not patient data. */
    consents: z.array(DraftConsentSchema),
    variants: z.array(z.string()),
    updatedAt: z.string(),
    expiresAt: z.string(),
});
/**
 * The decrypted answers. Returned **only** by `POST /public/drafts/current/resume`,
 * to the browser that holds the draft cookie, after the visitor asked to continue.
 */
export const DraftContentSchema = z.object({
    details: DraftDetailsSchema,
    insurance: DraftInsuranceSchema,
});
/* ----------------------------------------------------------------- responses ---- */
/**
 * `draftToken` is the mobile app's copy of the draft cookie (M18.1, ADR-034): present
 * only on `POST /public/drafts` and `GET /public/drafts/current`, and only when the
 * request sent `X-HA-Client: mobile`. The app sends it back as `X-HA-Draft`. Browsers
 * never receive it - their credential is the HttpOnly cookie.
 */
export const DraftTokenSchema = z.string().min(1).max(200);
export const DraftResponseSchema = z.object({
    data: z.object({ draft: DraftSummarySchema, draftToken: DraftTokenSchema.optional() }),
});
/**
 * `null` rather than a `404` when there is nothing to resume.
 *
 * The caller is a page-load prompt, and "no draft" is its ordinary answer, not an
 * error: a `404` here would make every first visit log an error and would push every
 * client into catching one.
 */
export const CurrentDraftResponseSchema = z.object({
    data: z.object({
        draft: DraftSummarySchema.nullable(),
        draftToken: DraftTokenSchema.optional(),
    }),
});
export const ResumeDraftResponseSchema = z.object({
    data: z.object({ draft: DraftSummarySchema, content: DraftContentSchema }),
});
//# sourceMappingURL=booking.js.map